import {useCallback, useLayoutEffect, useRef} from 'react';
import {useRevealOnView} from './useRevealOnView';
import styles from './Step10Viz.module.css';

// ─── SleepBlocksHero data ─────────────────────────────────────────────────

export type SleepBlockRow = {
  label: string;
  value: number;
  kept: boolean[]; // length 9 — true = tick remains at end of animation
};

export const BRANDED_BLOCK_ROWS: SleepBlockRow[] = [
  {label: 'Tonight', value: 37, kept: [true, true, true, true, true, true, true, true, true]},
  {label: 'Month 6', value: 11, kept: [false, false, false, true, true, true, false, false, false]},
  {label: 'Month 12', value: 6, kept: [false, false, false, false, true, false, false, false, false]},
];

const ROW_GRADIENTS = [
  // Tonight (deep purple, "apnea state") → Month 6 (transition) →
  // Month 12 (osa-accent-healthy, "destination"). Replaces the iOS
  // source's purple→gold ramp so the row tells a purple-to-green
  // story consistent with the OSA palette.
  ['#382365', '#2a1853'],
  ['#7a6ba8', '#5a4a85'],
  ['#005240', '#003a2c'],
];

/** 3-row hero: Tonight / Month 6 / Month 12 — sleep blocks become longer
 *  rest spans, breathing interruptions shrink dramatically. Steady-state
 *  visualization (we skip the slam-then-evict tick orchestration and
 *  render the final state with a stagger fade-in). */
export const SleepBlocksHero = ({rows, delayMs = 0}: {rows: SleepBlockRow[]; delayMs?: number}) => {
  const {ref, triggered} = useRevealOnView();
  return (
    <div ref={ref} className={styles.heroRoot}>
      <div className={styles.heroBox}>
        <div className={styles.heroHeader}>
          <span className={styles.heroHeaderText}>Breathing interruptions / hr</span>
        </div>
        {rows.map((row, ri) => {
          const rowDelay = delayMs + ri * 260;
          const [top, bot] = ROW_GRADIENTS[ri] ?? ROW_GRADIENTS[0];
          return (
            <div key={row.label} className={`${styles.barRow} ${ri > 0 ? styles.barRowSpacer : ''}`}>
              <div className={styles.rowLabelCol}>
                <span
                  className={`${styles.rowLabel} ${triggered ? styles.rowLabelOn : ''}`}
                  style={{transitionDelay: `${rowDelay}ms`}}
                >
                  {row.label.toUpperCase()}
                </span>
                <div className={styles.barTrack}>
                  <span
                    className={`${styles.barFill} ${triggered ? styles.barFillOn : ''}`}
                    style={{
                      background: `linear-gradient(180deg, ${top} 0%, ${bot} 100%)`,
                      transitionDelay: `${rowDelay}ms`,
                    }}
                  />
                  {row.kept.map((kept, si) =>
                    kept ? (
                      <span
                        key={si}
                        className={`${styles.tickMark} ${triggered ? styles.tickMarkOn : ''}`}
                        style={{
                          left: `${((si + 1) * 100) / 10 - 0.4}%`,
                          transitionDelay: `${rowDelay + 700 + si * 42}ms`,
                        }}
                      />
                    ) : null,
                  )}
                </div>
              </div>
              <span
                className={`${styles.rowValue} ${triggered ? styles.rowValueOn : ''}`}
                style={{transitionDelay: `${rowDelay + 200}ms`}}
              >
                {row.value}
                <span className={styles.rowValueUnit}>/hr</span>
              </span>
            </div>
          );
        })}
        <div className={styles.heroFootnote}>Illustrative comparison for educational purposes</div>
      </div>
    </div>
  );
};

// ─── Timeline ──────────────────────────────────────────────────────────────

export type TimelineMilestone = {
  time: string;
  moment: string;
  iconKind: 'spark' | 'zz' | 'coffee' | 'bed' | 'sunrise';
  color: string;
};

// Purple → green progression: starts deep purple (apnea state) and lands on
// osa-accent-healthy (treated, restored). Replaces the iOS source's
// purple → mauve → orange → gold ramp.
export const BRANDED_TIMELINE_MILESTONES: TimelineMilestone[] = [
  {
    time: 'Weeks 1–4',
    moment: 'Start the medication. Learn the routine with RISE in your corner.',
    iconKind: 'spark',
    color: '#382365',
  },
  {
    time: 'Month 2',
    moment: 'Snoring often fades. Mornings may start to feel different.',
    iconKind: 'zz',
    color: '#5e3f87',
  },
  {
    time: 'Month 4',
    moment: 'The afternoon fog lifts. You stop zoning out in meetings.',
    iconKind: 'coffee',
    color: '#7a6ba8',
  },
  {time: 'Month 6', moment: 'You wake up rested. Real recovery sleep, returned.', iconKind: 'bed', color: '#3c8da0'},
  {
    time: 'Month 12',
    moment: 'You wake up rested most days. Sleep has stopped being a thing you think about.',
    iconKind: 'sunrise',
    color: '#005240',
  },
];

export const COMPOUNDED_TIMELINE_MILESTONES: TimelineMilestone[] = [
  {
    time: 'Weeks 1–4',
    moment: 'Start your treatment. Build the daily routine with RISE alongside you.',
    iconKind: 'spark',
    color: '#382365',
  },
  {
    time: 'Month 2',
    moment: 'Many people start to notice changes in their snoring and how they feel waking up.',
    iconKind: 'zz',
    color: '#5e3f87',
  },
  {
    time: 'Month 4',
    moment: 'As weight comes off, breathing during sleep typically improves further. Daytime energy may follow.',
    iconKind: 'coffee',
    color: '#7a6ba8',
  },
  {
    time: 'Month 6',
    moment:
      'For those whose weight loss continues, sleep tends to feel less interrupted. Many report waking more rested.',
    iconKind: 'bed',
    color: '#3c8da0',
  },
  {
    time: 'Month 12',
    moment:
      'With sustained weight loss, sleep apnea severity can meaningfully decrease. Track your progress with your care team.',
    iconKind: 'sunrise',
    color: '#005240',
  },
];

const MilestoneIcon = ({kind}: {kind: TimelineMilestone['iconKind']}) => {
  const sw = 1.8;
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      {kind === 'spark' ? (
        <>
          <circle cx="12" cy="12" r="2.5" fill="#fff" />
          <path
            d="M12 4 v3 M12 17 v3 M4 12 h3 M17 12 h3 M6.5 6.5 l2 2 M15.5 15.5 l2 2 M17.5 6.5 l-2 2 M8.5 15.5 l-2 2"
            stroke="#fff"
            strokeWidth={sw}
            strokeLinecap="round"
            fill="none"
          />
        </>
      ) : null}
      {kind === 'zz' ? (
        <>
          <path
            d="M7 7 h6 L7 13 h6"
            stroke="#fff"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 13 h7 L12 21 h7"
            stroke="#fff"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : null}
      {kind === 'coffee' ? (
        <>
          <path
            d="M5 9 h12 v6 a4 4 0 0 1 -4 4 h-4 a4 4 0 0 1 -4 -4 v-6 z"
            fill="none"
            stroke="#fff"
            strokeWidth={sw}
            strokeLinejoin="round"
          />
          <path d="M17 12 h2 a2 2 0 0 1 0 4 h-2" fill="none" stroke="#fff" strokeWidth={sw} />
          <path d="M8 4 v2 M12 4 v2 M16 4 v2" stroke="#fff" strokeWidth={sw} strokeLinecap="round" />
        </>
      ) : null}
      {kind === 'bed' ? (
        <>
          <path
            d="M3 16 V9 h9 a4 4 0 0 1 4 4 v3 M3 16 h18 M21 16 V12"
            stroke="#fff"
            strokeWidth={sw}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <circle cx="7" cy="12" r="2" fill="#fff" />
        </>
      ) : null}
      {kind === 'sunrise' ? (
        <>
          <path d="M3 18 h18 M6 18 a6 6 0 0 1 12 0" stroke="#fff" strokeWidth={sw} strokeLinecap="round" fill="none" />
          <path d="M12 3 v3 M4 8 l2 2 M20 8 l-2 2" stroke="#fff" strokeWidth={sw} strokeLinecap="round" />
        </>
      ) : null}
    </svg>
  );
};

/** Vertical gradient trunk with N milestones (icon + time + moment). The
 *  trunk's bottom edge is dynamically positioned to land exactly at the
 *  last icon's vertical center via a ref-measured CSS variable — text
 *  wrapping varies row heights on web in ways the iOS fixed-height math
 *  doesn't predict. */
export const TimelineViz = ({milestones, delayMs = 0}: {milestones: TimelineMilestone[]; delayMs?: number}) => {
  const {ref: revealRef, triggered} = useRevealOnView();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const lastIconRef = useRef<HTMLSpanElement>(null);
  const perRow = 1600 / milestones.length;

  // Combine the reveal observer ref with our local measurement ref.
  const setRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node;
      (revealRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [revealRef],
  );

  // Compute the trunk's bottom inset so it ends at the last icon's center.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const lastIcon = lastIconRef.current;
    if (!root || !lastIcon) return;

    const measure = () => {
      const rootRect = root.getBoundingClientRect();
      const iconRect = lastIcon.getBoundingClientRect();
      const lastIconCenter = iconRect.top + iconRect.height / 2 - rootRect.top;
      const bottomInset = Math.max(0, rootRect.height - lastIconCenter);
      root.style.setProperty('--osa-trunk-bottom', `${bottomInset}px`);
    };

    measure();
    // Re-measure on resize since the last row's text wrap depends on viewport width.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(root);
    return () => ro?.disconnect();
  }, [milestones]);

  return (
    <div ref={setRootRef} className={styles.timelineRoot}>
      <span
        className={`${styles.trunk} ${triggered ? styles.trunkOn : ''}`}
        style={{transitionDelay: `${delayMs + 300}ms`}}
        aria-hidden="true"
      />
      {milestones.map((m, i) => {
        const rowDelay = delayMs + 600 + i * perRow;
        const isLast = i === milestones.length - 1;
        return (
          <div key={i} className={`${styles.timelineRow} ${!isLast ? styles.timelineRowGap : ''}`}>
            <span
              ref={isLast ? lastIconRef : undefined}
              className={`${styles.timelineIcon} ${triggered ? styles.timelineIconOn : ''}`}
              style={{
                background: m.color,
                boxShadow: `0 0 12px ${m.color}40`,
                transitionDelay: `${rowDelay}ms`,
              }}
            >
              <MilestoneIcon kind={m.iconKind} />
            </span>
            <div
              className={`${styles.timelineContent} ${triggered ? styles.timelineContentOn : ''}`}
              style={{transitionDelay: `${rowDelay + 120}ms`}}
            >
              <span className={styles.timelineTime} style={{color: m.color}}>
                {m.time}
              </span>
              <p className={styles.timelineMoment}>{m.moment}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};
